import clsx from 'clsx';
import Image from 'next/image';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { ImageCropModal } from '@/app/(main)/gallery/components/ImageCrop';
import { Tooltip } from '@/app/components/Tooltip';
import { tooltipStyles } from '@/app/components/Tooltip/styles';
import PdfPreview from '@/app/components/cards/FileCard/PdfPreview';
import { MAX_FILE_SIZE, MAX_FILE_SIZE_MB } from '@/consts/const';
import { useUploadFile } from '@/context/UploadFileProvider';
import { isFileSizeValid } from '@/hooks/useFileDrop/utils/validations';
import { useModalState } from '@/hooks/useModalState';
import { closeButtonVariants, fileEmbedWrapperVariants, previewFileCardRootVariants } from './styles';
import CrossImageIcon from '/public/icons/cross-image.svg';
import ImageErrorIcon from '/public/icons/image-error.svg';
import LoaderSmall from '/public/icons/loader-small.svg';

interface PreviewFileCardProps {
  file: File;
  size?: 'small' | 'large';
}

const sizeMap = {
  small: { width: 64, height: 64, loaderIconSize: 'w-8 h-8' },
  large: { width: 122, height: 122, loaderIconSize: 'w-12 h-12' },
};

export function PreviewFileCard({ file, size = 'small' }: PreviewFileCardProps) {
  const { onDeleteFile, onCropImage } = useUploadFile();
  const { isOpen, close, open } = useModalState();
  const [isImageLoading, setIsImageLoading] = useState(true);
  const isUnsupportedFile = useMemo(() => !isFileSizeValid(file, MAX_FILE_SIZE), [file]);

  const previewUrl = useMemo(() => {
    return URL.createObjectURL(file);
  }, [file]);

  useEffect(() => {
    return () => {
      if (previewUrl) {
        URL.revokeObjectURL(previewUrl);
      }
    };
  }, [previewUrl]);

  const onSave = (blob: Blob) => {
    close();
    onCropImage(blob, file.name);
  };

  const isImage = file.type.startsWith('image/');
  const isPdf = file.type === 'application/pdf';

  const dimensions = sizeMap[size];

  useEffect(() => {
    setIsImageLoading(true);
  }, [previewUrl]);

  const handleImageLoad = useCallback(() => {
    setIsImageLoading(false);
  }, []);

  const canOpenCropModal = isImage && size === 'small';

  return (
    <>
      <Tooltip
        key={file.name}
        isVisible={isUnsupportedFile}
        content={<div className={tooltipStyles()}>{MAX_FILE_SIZE_MB}MB limit exceeded</div>}
        placement="top"
      >
        <div onClick={canOpenCropModal ? open : undefined} className={previewFileCardRootVariants({ size })}>
          <div className={fileEmbedWrapperVariants({ size, isUnsupported: isUnsupportedFile })}>
            {isImageLoading && isImage && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-800 bg-opacity-25 rounded-[6px]">
                <LoaderSmall className={clsx(dimensions.loaderIconSize, 'text-white animate-spin')} />
              </div>
            )}
            {isImage && previewUrl && (
              <FileWrapper>
                <Image
                  width={dimensions.width}
                  height={dimensions.height}
                  src={previewUrl}
                  alt={file.name}
                  className="object-cover w-full h-full rounded-[6px]"
                  onLoad={handleImageLoad}
                />
                {isUnsupportedFile && <ImageErrorIcon className="absolute top-0 left-0 w-full h-full z-[5]" />}
              </FileWrapper>
            )}
            {isPdf && previewUrl && (
              <FileWrapper>
                <PdfPreview
                  fileUrl={previewUrl}
                  containerWidth={dimensions.width}
                  containerHeight={dimensions.height}
                />
                {isUnsupportedFile && <ImageErrorIcon className="absolute top-0 left-0 w-full h-full z-[5]" />}
              </FileWrapper>
            )}
          </div>
          <CloseButton onDeleteFile={onDeleteFile} file={file} size={size} />
        </div>
      </Tooltip>
      {canOpenCropModal && isOpen && (
        <ImageCropModal isOpen={isOpen} onClose={close} imageUrl={previewUrl} onSave={onSave} />
      )}
    </>
  );
}

const FileWrapper = ({ children }: { children: React.ReactNode }) => {
  return (
    <div className={clsx('relative flex items-center justify-center overflow-hidden', 'w-full h-full')}>{children}</div>
  );
};

const CloseButton = ({
  onDeleteFile,
  file,
  size = 'small',
}: {
  onDeleteFile: (file: File) => void;
  file: File;
  size?: 'small' | 'large';
}) => {
  return (
    <button
      className={closeButtonVariants({ size })}
      onClick={(e) => {
        e.stopPropagation();
        onDeleteFile(file);
      }}
      type="button"
    >
      <CrossImageIcon className="w-[10px] h-[10px] text-white fill-current" />
    </button>
  );
};